import type { HillChart } from '@hillchart/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, hillsApi } from './api';
import { queryKeys } from './query-keys';

/**
 * Mutation hook for the "Mark as Done" action.
 *
 * Sequence on success:
 *   1. The backend takes a `kind='final'` snapshot, sets `completedAt`, and
 *      broadcasts `hill:completed` over the WS room (then closes it with
 *      4423). The HTTP response carries the updated `HillChart` payload.
 *   2. We patch the detail cache with the response so any sibling component
 *      sees the lifecycle transition immediately.
 *   3. We invalidate the `hillsListPrefix` so both the home (`active`) and
 *      the archive (`done`) lists refetch on next access. The home will
 *      drop the hill, the archive will pick it up.
 *
 * 409 HILL_COMPLETED handling:
 *   The backend's complete endpoint is itself idempotent (returns 200 with
 *   the existing state on a second call). The 409 case here means a *write*
 *   raced with the completion - e.g. a teammate moved a dot just as we
 *   clicked the button. We swallow it as success: the hill is Done either
 *   way, and surfacing an error toast for what is effectively the desired
 *   end state would confuse the user. The detail cache invalidation forces
 *   the page to re-render with the fresh server state.
 */
export function useCompleteHill(hillId: string) {
  const qc = useQueryClient();

  return useMutation<HillChart, Error>({
    mutationFn: () => hillsApi.complete(hillId),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.hill(hillId), data);
      qc.invalidateQueries({ queryKey: queryKeys.hillsListPrefix() });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'HILL_COMPLETED') {
        // The hill is already Done (race with another teammate's click, or
        // a stale tab clicking twice). Force-refetch so the UI reflects the
        // fact and let the page re-render in read-only mode without
        // surfacing an error.
        qc.invalidateQueries({ queryKey: queryKeys.hill(hillId) });
        qc.invalidateQueries({ queryKey: queryKeys.hillsListPrefix() });
      }
      // Anything else (network, 500, validation) bubbles to the caller
      // which renders the error inline; we deliberately don't toast here
      // so the dialog can show a contextual message next to the action.
    },
  });
}
